import { FunctionComponent, KeyboardEvent, MouseEvent, useEffect, useMemo, useState } from "react";
import { ActionPlanAppointmentItem, ActionPlanViewModel } from "../../models/ActionPlan";
import { ValueCode } from "../../models/OverviewDashboard";
import ActionPlanService from "../../services/ActionPlanService";
import {
    getAppointmentParticipationInfo,
    registerCurrentUserToAppointment
} from "../../services/AppointmentParticipationService";
import { UpCareThemeCode } from "../../utils/UpCareThemes";
import useStableStringArray from "../../hooks/useStableStringArray";
import AppointmentRegistrationModal from "../AppointmentRegistration/AppointmentRegistrationModal";
import ActionPlanNewActionMenu from "./ActionPlanNewActionMenu";
import styles from "./ActionPlanSummaryWidget.module.css";

interface ActionPlanSummaryWidgetProps {
    plan?: ActionPlanViewModel;
    fhirBaseUrl?: string;
    value?: ValueCode;
    theme?: UpCareThemeCode;
    organizationReferences?: string[];
    poleOrganization?: {
        reference: string;
        name: string;
    };
    onNewAction?: () => void;
}

const EMPTY_PLAN: ActionPlanViewModel = {
    needs: [],
    proposals: [],
    appointments: []
};

const VALUE_CLASSES: Partial<Record<ValueCode, string>> = {
    bienveillance: styles.bienveillance,
    engagement: styles.engagement,
    "faire-equipe": styles.faireEquipe,
    respect: styles.respect
};

const ActionPlanSummaryWidget: FunctionComponent<ActionPlanSummaryWidgetProps> = ({
    plan,
    fhirBaseUrl,
    value,
    theme,
    organizationReferences = [],
    poleOrganization,
    onNewAction
}) => {
    const [loadedPlan, setLoadedPlan] = useState<ActionPlanViewModel>(EMPTY_PLAN);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [selectedAppointment, setSelectedAppointment] = useState<ActionPlanAppointmentItem>();
    const [refreshVersion, setRefreshVersion] = useState(0);

    const service = useMemo(
        () => fhirBaseUrl ? new ActionPlanService({ fhirBaseUrl }) : undefined,
        [fhirBaseUrl]
    );
    const stableOrganizationReferences = useStableStringArray(organizationReferences);

    useEffect(() => {
        if (plan) {
            setLoadedPlan(plan);
            setLoading(false);
            setError(undefined);
            return;
        }

        if (!service || !value || stableOrganizationReferences.length === 0) {
            setLoadedPlan(EMPTY_PLAN);
            setLoading(false);
            setError(undefined);
            return;
        }

        let mounted = true;
        setLoading(true);
        setError(undefined);

        service.search({
            value,
            theme,
            organizationReferences: stableOrganizationReferences,
            includePastAppointments: true
        })
            .then((result) => {
                if (mounted) setLoadedPlan(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) {
                    setLoadedPlan(EMPTY_PLAN);
                    setError("Impossible de charger le plan d'action.");
                }
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [plan, service, value, theme, stableOrganizationReferences, refreshVersion]);

    const firstAppointment = loadedPlan.appointments[0];

    const openAppointment = (appointment: ActionPlanAppointmentItem) => {
        setSelectedAppointment(appointment);
    };

    const handleCardKeyDown = (
        event: KeyboardEvent<HTMLElement>,
        appointment: ActionPlanAppointmentItem
    ) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openAppointment(appointment);
    };

    const handleRegistrationClick = (
        event: MouseEvent<HTMLButtonElement>,
        appointment: ActionPlanAppointmentItem
    ) => {
        event.stopPropagation();
        openAppointment(appointment);
    };

    const handleRegistrationConfirm = async (appointment: ActionPlanAppointmentItem) => {
        if (!fhirBaseUrl) {
            throw new Error("Le serveur FHIR n'est pas disponible pour l'inscription.");
        }

        const persisted = await registerCurrentUserToAppointment(fhirBaseUrl, appointment.raw);
        const participation = getAppointmentParticipationInfo(persisted);
        const updatedAppointment: ActionPlanAppointmentItem = {
            ...appointment,
            raw: persisted,
            participantCount: participation.participantCount,
            participantCapacity: participation.participantCapacity,
            isRegistered: participation.isRegistered
        };

        setLoadedPlan((current) => ({
            ...current,
            appointments: current.appointments.map((item) =>
                item.id === appointment.id ? updatedAppointment : item
            )
        }));
        setSelectedAppointment(undefined);
    };

    return (
        <>
            <section className={styles.planSummary}>
                <h2>Plan d'action</h2>

                {loading && <p>Chargement du plan d'action…</p>}
                {!loading && error && <p className={styles.error}>{error}</p>}

                {!loading && !error && firstAppointment && (
                    <article
                        className={`${styles.planItem} ${getValueClass(firstAppointment)}`}
                        role="button"
                        tabIndex={0}
                        aria-label={`Voir le détail de ${firstAppointment.title}`}
                        onClick={() => openAppointment(firstAppointment)}
                        onKeyDown={(event) => handleCardKeyDown(event, firstAppointment)}
                    >
                        <span className={styles.typeBadge}>
                            {firstAppointment.type ?? "Atelier / formation"}
                        </span>
                        <strong>{firstAppointment.title}</strong>
                        {firstAppointment.start && (
                            <small>{formatDate(firstAppointment.start)}</small>
                        )}

                        {isTraining(firstAppointment) && (
                            <div className={styles.trainingMeta}>
                                <span className={styles.capacity}>
                                    <span aria-hidden="true">👤</span>
                                    {firstAppointment.participantCount ?? 0} / {firstAppointment.participantCapacity ?? "—"} places
                                </span>

                                {firstAppointment.isRegistered ? (
                                    <span className={styles.registeredBadge}>✓ Inscrit</span>
                                ) : isPast(firstAppointment) ? (
                                    <span className={styles.fullBadge}>Terminé</span>
                                ) : isFull(firstAppointment) ? (
                                    <span className={styles.fullBadge}>Complet</span>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.registrationButton}
                                        onClick={(event) => handleRegistrationClick(event, firstAppointment)}
                                    >
                                        Je m'inscris
                                    </button>
                                )}
                            </div>
                        )}
                    </article>
                )}

                {!loading && !error && !firstAppointment && (
                    <p>Aucune action pour le moment.</p>
                )}

                <ActionPlanNewActionMenu
                    fhirBaseUrl={fhirBaseUrl}
                    value={value}
                    organizationReferences={organizationReferences}
                    poleOrganization={poleOrganization}
                    onOpen={onNewAction}
                    onCreated={() => setRefreshVersion((current) => current + 1)}
                />
            </section>

            <AppointmentRegistrationModal
                open={!!selectedAppointment}
                appointment={selectedAppointment}
                onClose={() => setSelectedAppointment(undefined)}
                onConfirm={handleRegistrationConfirm}
            />
        </>
    );
};

const getValueClass = (appointment: ActionPlanAppointmentItem): string => {
    const primaryValue = appointment.valueCodes[0] as ValueCode | undefined;
    return primaryValue ? VALUE_CLASSES[primaryValue] ?? "" : "";
};

const isTraining = (appointment: ActionPlanAppointmentItem): boolean =>
    (appointment.type ?? "").toLocaleLowerCase("fr-FR").includes("formation");

const isFull = (appointment: ActionPlanAppointmentItem): boolean =>
    appointment.participantCapacity !== undefined &&
    (appointment.participantCount ?? 0) >= appointment.participantCapacity;

const isPast = (appointment: ActionPlanAppointmentItem): boolean => {
    const reference = appointment.end ?? appointment.start;
    if (!reference) return false;
    return new Date(reference).getTime() < Date.now();
};

const formatDate = (value: string): string => new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
}).format(new Date(value));

export default ActionPlanSummaryWidget;
